// src/lib/vertraege.js
// Vertraege je Station (Migration 45): Wartungsvertrag, Betriebsfuehrung,
// Bereitschaft. Nur Buero und Admin duerfen sie lesen (Preise) - der Monteur
// bekommt ueber vertrag_absprachen() nur die Absprachen ohne Preise.

import { supabase, withTimeout } from './supabase';

export const VERTRAGSARTEN = [
  { art: 'wartung',          text: 'Wartungsvertrag' },
  { art: 'betriebsfuehrung', text: 'Betriebsführungsvertrag' },
  { art: 'bereitschaft',     text: 'Bereitschaft (Notdienst)' },
];

// Zyklen: im Vertragsmuster Buchbusch steht "4-jährig" - deshalb bis 5.
// Nur 1-4 Jahre (Anna-Lena 18.09.2026). Alte Werte zeigt der Editor weiter an.
export const ZYKLUS_JAHRE = [1, 2, 3, 4];

// Leistungsumfang - die Oberbegriffe aus "V. Leistungsumfang" der ETB-
// Vertragsvorlage (Anna-Lena, 17.09.2026), jeder einzeln waehlbar.
// Schluessel bleiben stabil (in station_vertraege.leistungen gespeichert).
export const LEISTUNGEN = [
  { key: 'notstrom',      text: 'Notstromaggregat' },
  { key: 'ms_anlage',     text: 'Mittelspannungsschaltanlage' },
  { key: 'nshv',          text: 'Niederspannungshauptverteilung' },
  { key: 'trafo',         text: 'Transformator' },
  { key: 'raeume',        text: 'Schalt- und Traforäume' },
  { key: 'erdung',        text: 'Erdungsanlage' },
  { key: 'zubehoer',      text: 'Zubehör Transformatorstation' },
  { key: 'thermografie',  text: 'Elektrothermografie' },
  { key: 'umz',           text: 'Überstromschutzrelais (Schutzprüfung)' },
  { key: 'blindleistung', text: 'Blindleistungsregelanlage' },
];
// Frueher war die NSHV eine Auswahl ("inklusive"/"bauseits") - "bauseits" heisst nicht enthalten.
export function leistungGewaehlt(l, key) {
  const v = (l || {})[key];
  return key === 'nshv' ? v === true || v === 'inklusive' : !!v;
}
export const OELPROBE_VERTRAG = [
  { value: 'inklusive',    label: 'Ölprobe inklusive' },
  { value: 'nach_aufwand', label: 'Ölprobe nach Aufwand' },
  { value: 'nein',         label: 'keine Ölprobe' },
];

export const ABSCHALTZEITEN = [
  { key: 'arbeitszeit', text: 'normale Arbeitszeit' },
  { key: 'abends',      text: 'abends' },
  { key: 'nachts',      text: 'nachts' },
  { key: 'wochenende',  text: 'Wochenende' },
  { key: 'feiertag',    text: 'Feiertag' },
];
export const MONATE_KURZ = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
export const MONATE_LANG = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

// Tabelle fehlt = Migration 45 noch nicht ausgefuehrt.
export function istMigrationFehlt(e) {
  const m = String(e?.message || '');
  return e?.code === '42P01' || e?.code === 'PGRST205'
    || (/station_vertraege/.test(m) && /does not exist|schema cache|could not find/i.test(m));
}

export async function vertraegeDerStation(stationId) {
  const { data, error } = await withTimeout(
    supabase.from('station_vertraege').select('*').eq('station_id', stationId), 20000, 'Verträge laden');
  if (error) throw error;
  const jeArt = {};
  (data || []).forEach(v => { jeArt[v.art] = v; });
  return jeArt;
}

// Alle Vertraege (fuer die Uebersicht), seitenweise.
export async function alleVertraege() {
  const alle = [];
  for (let von = 0; ; von += 1000) {
    const { data, error } = await withTimeout(
      supabase.from('station_vertraege').select('*').order('id').range(von, von + 999), 30000, 'Verträge laden');
    if (error) throw error;
    alle.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  return alle;
}

const LEER_ZU_NULL = ['beginn', 'erste_wartung', 'erste_sichtpruefung', 'letzte_sichtpruefung', 'zahlungsziel',
  'abschalt_notiz', 'hinweis_monteur', 'besondere_vereinbarungen', 'notiz'];
const ZAHLEN = ['laufzeit_jahre', 'wartungszyklus_jahre', 'sichtpruefung_jahre', 'preisbindung_jahre',
  'entgelt_wartung', 'entgelt_sichtpruefung'];

function bereinigen(v) {
  const z = { ...v };
  delete z.created_at; delete z.updated_at;
  LEER_ZU_NULL.forEach(k => { if (k in z && (z[k] === '' || z[k] === undefined)) z[k] = null; });
  ZAHLEN.forEach(k => {
    if (!(k in z)) return;
    if (z[k] === '' || z[k] == null) { z[k] = null; return; }
    // "2.315,00" / "2.315" (deutsch, Punkt = Tausender) oder 2315.5 (Zahl).
    const t = String(z[k]).trim().replace(/\s|€/g, '');
    const n = typeof z[k] === 'number' ? z[k]
      : t.includes(',') ? Number(t.replace(/\./g, '').replace(',', '.'))
      : /^\d{1,3}(\.\d{3})+$/.test(t) ? Number(t.replace(/\./g, ''))
      : Number(t);
    z[k] = Number.isFinite(n) ? n : null;
  });
  ['laufzeit_jahre', 'wartungszyklus_jahre', 'sichtpruefung_jahre', 'preisbindung_jahre']
    .forEach(k => { if (z[k] != null) z[k] = Math.round(z[k]); });
  return z;
}

// Speichert einen Vertrag (anlegen oder aendern) und gibt die gespeicherte Zeile zurueck.
export async function vertragSpeichern(stationId, art, felder) {
  const { data: sitzung } = await supabase.auth.getSession();
  const zeile = bereinigen({ ...felder, station_id: stationId, art, geaendert_von: sitzung?.session?.user?.id || null });
  if (!zeile.id) delete zeile.id;
  const { data, error } = await withTimeout(
    supabase.from('station_vertraege').upsert(zeile, { onConflict: 'station_id,art' }).select().single(),
    20000, 'Vertrag speichern');
  if (error) throw error;
  return data;
}

// PDF hochladen: Pfad je Station und Art, alte Datei wird ersetzt.
export async function vertragPdfHochladen(stationId, art, datei) {
  if (!datei) return null;
  if (datei.type && datei.type !== 'application/pdf') throw new Error('Bitte eine PDF-Datei auswählen.');
  const pfad = `${stationId}/${art}-${Date.now()}.pdf`;
  const { error } = await withTimeout(
    supabase.storage.from('vertraege').upload(pfad, datei, { contentType: 'application/pdf', upsert: false }),
    120000, 'PDF hochladen');
  if (error) throw error;
  return { pdf_pfad: pfad, pdf_name: datei.name || 'Vertrag.pdf' };
}

export async function vertragPdfLoeschen(pfad) {
  if (!pfad) return;
  try { await supabase.storage.from('vertraege').remove([pfad]); } catch { /* bleibt liegen, stoert nicht */ }
}

// Oeffnet das PDF ueber einen kurzlebigen Link (der Speicher ist nicht oeffentlich).
export async function vertragPdfOeffnen(pfad) {
  // Fenster sofort oeffnen - sonst blockiert der Browser das Popup nach dem await.
  const fenster = window.open('', '_blank');
  try {
    const { data, error } = await withTimeout(
      supabase.storage.from('vertraege').createSignedUrl(pfad, 300), 20000, 'PDF-Link');
    if (error) throw error;
    // Kein Fenster (Popup blockiert): NICHT die App selbst wegnavigieren -
    // ungespeicherte Eingaben waeren weg.
    if (!fenster) throw new Error('Der Browser hat das neue Fenster blockiert. Bitte Popups für diese Seite erlauben.');
    fenster.location.href = data.signedUrl;
  } catch (e) {
    if (fenster) fenster.close();
    throw e;
  }
}

// --- Absprachen fuer den Monteur (ohne Preise) --------------------------------
const ABSPRACHEN_CACHE = 'etb-absprachen-';
export function absprachenAusCache(stationId) {
  try { return JSON.parse(localStorage.getItem(ABSPRACHEN_CACHE + stationId) || 'null'); } catch { return null; }
}
export async function absprachenLaden(stationId) {
  const { data, error } = await withTimeout(
    supabase.rpc('vertrag_absprachen', { p_station_id: stationId }), 15000, 'Absprachen laden');
  if (error) throw error;
  try { localStorage.setItem(ABSPRACHEN_CACHE + stationId, JSON.stringify(data || null)); } catch { /* nur Komfort */ }
  return data || null;
}

// --- Faelligkeit ----------------------------------------------------------------
export function datumAus(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
const monatsErster = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const plusJahre = (d, j) => new Date(d.getFullYear() + j, d.getMonth(), 1);

// Naechste Sichtpruefung: ab der letzten Sichtpruefung oder Wartung (die
// Wartung schliesst die Sichtpruefung ein) plus Intervall, sonst der erste
// Termin laut Vertrag. Faellt sie nicht vor die naechste Wartung, entfaellt sie.
export function naechsteSichtpruefung(vertrag, letzteWartung, naechsteWartung) {
  if (!vertrag?.vorhanden || !vertrag.sichtpruefung_jahre) return null;
  const letzteSicht = datumAus(vertrag.letzte_sichtpruefung);
  const basis = [letzteSicht, letzteWartung].filter(Boolean).sort((a, b) => b - a)[0];
  const erste = datumAus(vertrag.erste_sichtpruefung) ? monatsErster(datumAus(vertrag.erste_sichtpruefung)) : null;
  let termin = basis ? plusJahre(basis, vertrag.sichtpruefung_jahre) : erste;
  // Noch nie eine Sichtpruefung: nicht vor dem ersten Termin laut Vertrag.
  if (termin && erste && !letzteSicht && termin < erste) termin = erste;
  if (!termin) return null;
  if (naechsteWartung && termin >= monatsErster(naechsteWartung)) return null;
  return termin;
}

// Vorbelegung fuer "Wartung planen" aus dem Vertrag.
export function planungAusVertrag(vertrag) {
  if (!vertrag?.vorhanden) return null;
  const l = vertrag.leistungen || {};
  const teile = [];
  const zeiten = (vertrag.abschaltzeiten || []).map(k => ABSCHALTZEITEN.find(a => a.key === k)?.text).filter(Boolean);
  if (zeiten.length) teile.push('Abschaltung: ' + zeiten.join(', '));
  if (vertrag.abschalt_notiz) teile.push(vertrag.abschalt_notiz);
  if (vertrag.freigabe_schriftlich) teile.push('schriftliche Freigabe des Kunden nötig');
  if (vertrag.hinweis_monteur) teile.push(vertrag.hinweis_monteur);
  return {
    oelprobe: l.oelprobe === 'inklusive' ? 'normal' : null,
    umz: !!l.umz,
    bemerkung: teile.join(' · '),
  };
}
